import React from "react";

import { AjaxManager } from "@impero-com/common/utils/ajax";
import { assertNever } from "@impero-com/common/utils/obj";
import { Typography } from "antd";

import { ControlProgramOverview } from "api/generated/model";
import { ControlProgramTypeahead } from "components/controlProgramTypeahead";
import { MultiInputHelper } from "utils/multiInputHelper";

import { BaseFormDisplay } from "./formDisplay";

import { BaseFormField } from ".";

const helper: MultiInputHelper<ControlProgramOverview, string> =
    new MultiInputHelper({
        isEqual: (a: ControlProgramOverview, b: ControlProgramOverview) =>
            a.id === b.id,
        isBlank: (_cp: ControlProgramOverview) => false,
        display: (cp: ControlProgramOverview) => cp.name,
    });

type MultiControlProgramTypeaheadProps =
    | ({ type: "CREATE" } & WrappedWriteProps)
    | ({ type: "READ_ONLY" } & WrappedReadProps);

type Wrapped<T> = T & { label: string };

interface ReadProps {
    name: string;
    values?: ControlProgramOverview[];
}

type WrappedReadProps = Wrapped<ReadProps>;

interface WriteProps extends ReadProps {
    onSelect: (v: ControlProgramOverview | undefined) => void;
    ajaxManager: AjaxManager;
}

type WrappedWriteProps = Wrapped<WriteProps>;

class MultiControlProgramTypeahead extends React.PureComponent<MultiControlProgramTypeaheadProps> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const { label, name, values } = props;
                return (
                    <WrappedReadOnlyControlProgramTypeahead
                        label={label}
                        name={name}
                        values={values}
                    />
                );
            }
            case "CREATE": {
                const { label, name, values, onSelect, ajaxManager } = props;
                return (
                    <WrappedCreateControlProgramTypeahead
                        label={label}
                        name={name}
                        values={values}
                        onSelect={onSelect}
                        ajaxManager={ajaxManager}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

type RawReadOnlyControlProgramTypeaheadProps = ReadProps;

class RawReadOnlyControlProgramTypeahead extends React.PureComponent<RawReadOnlyControlProgramTypeaheadProps> {
    render() {
        const { value, type } = helper.deriveReadOnlyValueAndType(
            this.props.values
        );
        return <Typography.Text type={type}>{value}</Typography.Text>;
    }
}

interface RawEditableControlProgramTypeaheadProps extends WriteProps {
    autoFocus?: boolean;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

interface RawEditableControlProgramTypeaheadState {
    derivedValue: ControlProgramOverview | undefined;
    trackedValue: ControlProgramOverview | undefined;
    placeholder: string;
}

class RawEditableControlProgramTypeahead extends React.Component<
    RawEditableControlProgramTypeaheadProps,
    RawEditableControlProgramTypeaheadState
> {
    state: RawEditableControlProgramTypeaheadState = {
        derivedValue: undefined,
        trackedValue: undefined,
        placeholder: "",
    };

    static getDerivedStateFromProps(
        props: RawEditableControlProgramTypeaheadProps,
        prevState: RawEditableControlProgramTypeaheadState
    ): RawEditableControlProgramTypeaheadState | null {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            props.values
        );
        if (value !== prevState.derivedValue) {
            return {
                derivedValue: value,
                trackedValue: value,
                placeholder,
            };
        }
        return null;
    }

    render() {
        const { props, state } = this;
        const { ajaxManager } = props;
        const { trackedValue, placeholder } = state;

        return (
            <ControlProgramTypeahead
                id={"demo-multi-control-program-typeahead"}
                ajaxManager={ajaxManager}
                onSelect={this.onSelect}
                value={trackedValue}
                placeholder={placeholder}
            />
        );
    }

    onSelect = (controlProgram: ControlProgramOverview) => {
        this.setState({
            trackedValue: controlProgram,
        });
        this.props.onSelect(controlProgram);
    };
}

class WrappedReadOnlyControlProgramTypeahead extends React.PureComponent<WrappedReadProps> {
    render() {
        const { label, name, values } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawReadOnlyControlProgramTypeahead
                    name={name}
                    values={values}
                />
            </BaseFormDisplay>
        );
    }
}

class WrappedCreateControlProgramTypeahead extends React.PureComponent<WrappedWriteProps> {
    render() {
        const { label, name, values, onSelect, ajaxManager } = this.props;
        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableControlProgramTypeahead
                        name={name}
                        values={values}
                        onSelect={onSelect}
                        ajaxManager={ajaxManager}
                    />
                )}
                hasFeedback={false}
                label={label}
            />
        );
    }
}

export { MultiControlProgramTypeahead, type MultiControlProgramTypeaheadProps };
